/**
 * Quick Add (custom bottom-sheet) — Maison Maia
 *
 * Pairs with snippets/quick-add-custom.liquid and the reference CSS. Mirrors the
 * reference behaviour (HorizonQuickAddOptions):
 *   - Capture-phase click delegation so the card link never navigates.
 *   - Opening adds `.active` to the trigger + sheet AND `.quick-add-open` to the
 *     parent `.product-grid__item` / `.resource-list__slide` (so the sheet isn't
 *     clipped — the reference CSS relies on this).
 *   - Selecting an option applies the reference's inline "selected" styling.
 *   - Sold-out options are rendered disabled by the Liquid.
 *
 * Difference from the reference: the actual add-to-cart uses Horizon's native
 * CartLinesUpdateEvent (from @shopify/events) instead of a `cart:update`
 * CustomEvent, because THIS theme's cart drawer only listens for the former —
 * it then auto-opens and refreshes the drawer for us (see cart-drawer.js).
 *
 * Loaded once via the theme importmap (module URLs dedupe on execution).
 */

import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

const routes = (window.Theme && window.Theme.routes) || {};
const CART_ADD_URL = routes.cart_add_url || '/cart/add.js';
const CART_URL = routes.cart_url || '/cart';

class HorizonQuickAddOptions {
  constructor() {
    /** @type {Record<string, {variants: Array<any>}>} */
    this.productData = {};
    this.init();
    this.initObserver();
  }

  init() {
    // Capture phase so we intercept before the card's link/navigation listeners.
    document.addEventListener(
      'click',
      (e) => {
        const target = /** @type {HTMLElement} */ (e.target);

        // Open / toggle
        const trigger = target.closest('[data-product-card-quick-add]');
        if (trigger) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          const sheet = trigger.closest('.product-card-quick-add')?.querySelector('[data-quick-add-sheet]');
          if (sheet && sheet.classList.contains('active')) this.closeSheet(sheet);
          else this.openSheet(trigger);
          return;
        }

        // Close (X or overlay)
        const closer = target.closest('[data-sheet-close]') || target.closest('[data-sheet-overlay]');
        if (closer) {
          e.preventDefault();
          e.stopPropagation();
          this.closeSheet(closer.closest('[data-quick-add-sheet]'));
          return;
        }

        // Add to basket
        const submitBtn = target.closest('[data-submit-button]');
        if (submitBtn) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          const sheet = submitBtn.closest('[data-quick-add-sheet]');
          const parent = sheet?.closest('.product-card-quick-add');
          if (parent?.dataset.product) this.handleAddToCart(sheet, parent.dataset.product);
          return;
        }
      },
      true
    );

    // Selected-option styling
    document.addEventListener('change', (e) => {
      const el = /** @type {HTMLElement} */ (e.target);
      if (el.matches('input[type="radio"]') && el.closest('[data-product-options]')) {
        this.updateVisualSelection(/** @type {HTMLInputElement} */ (el));
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const openSheet = document.querySelector('[data-quick-add-sheet].active');
      if (openSheet) this.closeSheet(openSheet);
    });
  }

  // Pick up products added to the DOM later (filtering, pagination, swatch swaps).
  initObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.querySelector?.('[data-product-card-quick-add]')) {
            node.querySelectorAll('.product-card-quick-add').forEach((card) => this.loadProductData(card));
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    document.querySelectorAll('.product-card-quick-add').forEach((card) => this.loadProductData(card));
  }

  // Prefer the variant map embedded by the Liquid; fall back to the product JSON.
  loadProductData(card) {
    const productId = card.dataset.product;
    const handle = card.dataset.handle;
    if (!productId || this.productData[productId]) return;

    const script = card.querySelector('[data-quick-add-variants]');
    if (script) {
      try {
        this.productData[productId] = { variants: JSON.parse(script.textContent) };
        return;
      } catch (error) {
        /* fall through to fetch */
      }
    }

    if (handle) {
      fetch(`/products/${handle}.js`)
        .then((res) => res.json())
        .then((data) => {
          this.productData[productId] = data;
        })
        .catch((error) => console.warn('[quick-add-custom] product JSON error', error));
    }
  }

  openSheet(trigger) {
    const card = trigger.closest('.product-card-quick-add');
    const sheet = card?.querySelector('[data-quick-add-sheet]');
    if (!sheet) return;

    // Close any other open sheet + clear its parent state.
    document.querySelectorAll('[data-quick-add-sheet].active').forEach((s) => {
      if (s !== sheet) this.closeSheet(s);
    });
    document.querySelectorAll('[data-product-card-quick-add].active').forEach((t) => t.classList.remove('active'));

    // Lift the parent card so the sheet isn't clipped (reference CSS hook).
    this.#setParentOpen(trigger, true);

    trigger.classList.add('active');
    sheet.classList.add('active');
    sheet.setAttribute('aria-hidden', 'false');
  }

  closeSheet(sheet) {
    if (!sheet) return;
    const card = sheet.closest('.product-card-quick-add');
    const trigger = card?.querySelector('[data-product-card-quick-add]');

    sheet.classList.remove('active');
    sheet.setAttribute('aria-hidden', 'true');
    if (trigger) {
      trigger.classList.remove('active');
      this.#setParentOpen(trigger, false);
    }
  }

  /** Toggle `.quick-add-open` on the enclosing grid item / carousel slide. */
  #setParentOpen(trigger, isOpen) {
    ['.resource-list__slide', '.product-grid__item'].forEach((selector) => {
      const parent = trigger.closest(selector);
      if (parent) parent.classList.toggle('quick-add-open', isOpen);
    });
  }

  async handleAddToCart(sheet, productId) {
    const submitBtn = sheet.querySelector('[data-submit-button]');
    const options = sheet.querySelector('[data-product-options]');
    const card = sheet.closest('.product-card-quick-add');

    // Make sure we have variant data (embedded or fetched).
    if (!this.productData[productId] && card) {
      this.loadProductData(card);
      if (!this.productData[productId] && card.dataset.handle) {
        try {
          this.productData[productId] = await fetch(`/products/${card.dataset.handle}.js`).then((r) => r.json());
        } catch (error) {
          console.warn('[quick-add-custom] Could not load product data', error);
          return;
        }
      }
    }

    const product = this.productData[productId];
    const selected = Array.from(sheet.querySelectorAll('input[type="radio"]:checked')).map((i) => i.value);

    // Nothing (fully) selected yet — prompt for a choice.
    if (!product || selected.length === 0) {
      if (options) options.classList.add('needs-selection');
      return;
    }

    const variant = product.variants.find(
      (v) => v.options.length === selected.length && v.options.every((opt, i) => opt === selected[i])
    );
    if (!variant || variant.available === false) return;
    if (options) options.classList.remove('needs-selection');

    const originalLabel = submitBtn?.querySelector('span:first-child')?.textContent;
    if (submitBtn) submitBtn.setAttribute('disabled', '');
    if (submitBtn?.querySelector('span:first-child')) submitBtn.querySelector('span:first-child').textContent = 'Adding…';

    const ok = await this.#addToCart(variant.id, sheet);

    if (submitBtn) {
      submitBtn.removeAttribute('disabled');
      const first = submitBtn.querySelector('span:first-child');
      if (first && originalLabel != null) first.textContent = originalLabel;
    }
    if (ok) {
      this.closeSheet(sheet);
      this.#openCartDrawer();
    }
  }

  /**
   * Force-open the cart drawer after adding. Horizon only auto-opens when the
   * `auto_open_cart_drawer` setting is on (cart-drawer.js), so we open it
   * ourselves. Handles both Horizon cart-drawer structures. The cart contents
   * are refreshed by the CartLinesUpdateEvent.
   */
  #openCartDrawer() {
    // 1) Newer Horizon: <theme-drawer id="cart-drawer"> owns the dialog.
    const themeDrawer = /** @type {any} */ (document.querySelector('theme-drawer#cart-drawer'));
    if (themeDrawer && typeof themeDrawer.open === 'function') {
      if (!themeDrawer.isOpen) themeDrawer.open();
      return;
    }

    // 2) Older Horizon (reference): <cart-drawer-component> exposes open().
    const component = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    if (component && typeof component.open === 'function') {
      component.open();
      return;
    }

    // 3) Fallback: open the cart <dialog> directly.
    const dialog = /** @type {HTMLDialogElement | null} */ (
      document.querySelector('#cart-drawer dialog, dialog.cart-drawer__dialog')
    );
    if (dialog && !dialog.open) {
      try {
        dialog.showModal();
      } catch (error) {
        dialog.setAttribute('open', '');
      }
    }
  }

  /** Adds to cart the same way product-form.js does, so the drawer auto-opens + refreshes. */
  async #addToCart(variantId, sheet) {
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');

    const sectionIds = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const id = el.dataset.sectionId;
      if (id && !sectionIds.includes(id)) sectionIds.push(id);
    });
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    (sheet || document).dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const cfg = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(CART_ADD_URL, { ...cfg, headers: { ...cfg.headers, Accept: 'text/html' } });
      const data = await response.json();

      if (data.status) {
        deferred.reject(new Error(data.message || 'Add to cart failed'));
        throw new Error(data.description || data.message || 'Add to cart failed');
      }

      const cart = await fetch(`${CART_URL}.js`).then((res) => res.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'quick-add-custom',
          sourceId: 'quick-add-custom',
          itemCount: 1,
          sections: data.sections,
          didError: false,
        },
      });
      return true;
    } catch (error) {
      if (error && error.name !== 'AbortError') console.warn('[quick-add-custom] Add to cart failed', error);
      return false;
    }
  }

  updateVisualSelection(radio) {
    const group = radio.closest('.option-values');
    if (!group) return;
    group.querySelectorAll('.option-value').forEach((label) => {
      label.style.background = '';
      label.style.color = '';
    });
    const label = radio.closest('.option-value');
    if (label) {
      label.style.background = 'var(--color-foreground)';
      label.style.color = 'var(--color-background)';
    }
    const options = radio.closest('[data-product-options]');
    if (options) options.classList.remove('needs-selection');
  }
}

const initQuickAdd = () => {
  if (!window.horizonQuickAdd) window.horizonQuickAdd = new HorizonQuickAddOptions();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuickAdd);
} else {
  initQuickAdd();
}
